/**
 * FunnelPage — W66.H Real Funnel Dashboard.
 *
 * Horizontal SVG funnel: MQL → SQL → Opportunity → Won.
 * Stage colors per canon:
 *   MQL:         Gray    #6B7280
 *   SQL:         Yellow  #EAB308
 *   Opportunity: Green   #16A34A
 *   Won:         Green   #16A34A
 * Conversion % callouts in neutral dark gray #374151 (W66.X12 canon swap).
 *
 * Data from GET /api/funnel?period=current_quarter|last_quarter|ytd.
 * Tenant 1. Manager/Exec see all reps; AE/BDR see own accounts only.
 *
 * W66.H — approved Bryan K. Short 2026-05-31
 */

import { useEffect, useState, useCallback } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api } from "@/lib/session";

// ─── Types ─────────────────────────────────────────────────────────────────────

type Period = "current_quarter" | "last_quarter" | "ytd";

interface AccountRef {
  id: number;
  name: string;
}

interface FunnelStage {
  key: "MQL" | "SQL" | "Opportunity" | "Won";
  label: string;
  count: number;
  accounts: AccountRef[];
}

interface FunnelConversions {
  mql_to_sql: number;
  sql_to_opp: number;
  opp_to_won: number;
  overall: number;
}

interface FunnelResponse {
  period: { start: string; end: string; label: string };
  stages: FunnelStage[];
  conversions: FunnelConversions;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

// W66.X12: STAGE_COLORS aligned to canon 5-color palette per CANON_VISUALS_LOCKED.md.
// Removed the prohibited brand-only deep green. Replaced with canon green #16A34A.
// Funnel ramp: gray (upstream) → yellow (qualifying) → orange (in-flight) → green (won).
const STAGE_COLORS: Record<string, string> = {
  MQL: "#6B7280",
  SQL: "#EAB308",
  Opportunity: "#EA580C",
  Won: "#16A34A",
};

const STAGE_TEXT: Record<string, string> = {
  MQL: "#FFFFFF",
  SQL: "#1C1917",
  Opportunity: "#FFFFFF",
  Won: "#FFFFFF",
};

// W66.X12: teal (#0F766E) is off-canon. Conversion arrows now use neutral dark gray.
const ARROW_COLOR = "#374151";

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: "current_quarter", label: "Current Quarter" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "ytd", label: "Year to Date" },
];

// ─── SVG Funnel ───────────────────────────────────────────────────────────────

const SVG_W = 900;
const SVG_H = 160;
const TRAP_GAP = 6;

// Each trapezoid shrinks from left to right.
// Full height, narrowing top and bottom.
// We use 4 equal-width slots; the trapezoid shape creates the funnel illusion.
// leftY and rightY control the vertical inset at left and right edges.
function trapPath(
  x: number,
  w: number,
  leftInset: number,
  rightInset: number
): string {
  const top = leftInset;
  const bottom = SVG_H - leftInset;
  const topR = rightInset;
  const bottomR = SVG_H - rightInset;
  return `M ${x},${top} L ${x + w},${topR} L ${x + w},${bottomR} L ${x},${bottom} Z`;
}

function FunnelSVG({
  stages,
  conversions,
  onStageClick,
  activeKey,
}: {
  stages: FunnelStage[];
  conversions: FunnelConversions;
  onStageClick: (key: string) => void;
  activeKey: string | null;
}) {
  const totalW = SVG_W;
  const n = stages.length;
  const slotW = (totalW - TRAP_GAP * (n - 1)) / n;

  // Inset per stage: stage 0 = 0, stage 3 = ~30% from each edge
  const insets = [0, 10, 20, 30];

  const convPcts = [
    conversions.mql_to_sql,
    conversions.sql_to_opp,
    conversions.opp_to_won,
  ];

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        style={{ width: "100%", minWidth: 480, height: "auto", display: "block" }}
        aria-label="Funnel: MQL to Won"
        role="img"
      >
        {stages.map((stage, i) => {
          const x = i * (slotW + TRAP_GAP);
          const leftInset = insets[i] ?? 0;
          const rightInset = insets[i + 1] ?? leftInset + 10;
          const path = trapPath(x, slotW, leftInset, rightInset);
          const color = STAGE_COLORS[stage.key] ?? "#6B7280";
          const textColor = STAGE_TEXT[stage.key] ?? "#FFFFFF";
          const isActive = activeKey === stage.key;
          const cx = x + slotW / 2;
          const cy = SVG_H / 2;

          return (
            <g
              key={stage.key}
              onClick={() => onStageClick(stage.key)}
              style={{ cursor: "pointer" }}
              role="button"
              aria-label={`${stage.label}: ${stage.count} accounts`}
            >
              <path
                d={path}
                fill={color}
                opacity={isActive ? 1 : 0.88}
                stroke={isActive ? "#0B0B0F" : "none"}
                strokeWidth={isActive ? 2.5 : 0}
              />
              {/* Stage label */}
              <text
                x={cx}
                y={cy - 16}
                textAnchor="middle"
                fontSize={11}
                fontWeight={700}
                letterSpacing={1.2}
                fill={textColor}
                fontFamily="Inter, system-ui, sans-serif"
                style={{ textTransform: "uppercase", pointerEvents: "none" }}
              >
                {stage.label}
              </text>
              {/* Count */}
              <text
                x={cx}
                y={cy + 10}
                textAnchor="middle"
                fontSize={28}
                fontWeight={800}
                fill={textColor}
                fontFamily="DM Sans, Inter, system-ui, sans-serif"
                style={{ pointerEvents: "none" }}
              >
                {stage.count}
              </text>
              {/* Conversion arrow above gap between this and next */}
              {i < n - 1 && (
                <g>
                  {/* Arrow line */}
                  <line
                    x1={x + slotW + TRAP_GAP / 2 - 16}
                    y1={SVG_H / 2}
                    x2={x + slotW + TRAP_GAP / 2 + 12}
                    y2={SVG_H / 2}
                    stroke={ARROW_COLOR}
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                  <polygon
                    points={`${x + slotW + TRAP_GAP / 2 + 10},${SVG_H / 2 - 4} ${x + slotW + TRAP_GAP / 2 + 16},${SVG_H / 2} ${x + slotW + TRAP_GAP / 2 + 10},${SVG_H / 2 + 4}`}
                    fill={ARROW_COLOR}
                  />
                  {/* Conversion % label above arrow */}
                  <text
                    x={x + slotW + TRAP_GAP / 2}
                    y={SVG_H / 2 - 10}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill={ARROW_COLOR}
                    fontFamily="Inter, system-ui, sans-serif"
                    style={{ pointerEvents: "none" }}
                  >
                    {convPcts[i] != null ? `${convPcts[i]}%` : "—"}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Drill-down panel ─────────────────────────────────────────────────────────

function DrillPanel({
  stage,
  onClose,
}: {
  stage: FunnelStage | null;
  onClose: () => void;
}) {
  if (!stage) return null;
  const color = STAGE_COLORS[stage.key] ?? "#6B7280";

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: "20px 18px",
        minWidth: 240,
        maxWidth: 320,
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: color,
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0B0B0F" }}>
            {stage.label}
          </span>
          <span
            style={{
              display: "inline-block",
              background: color,
              color: STAGE_TEXT[stage.key] ?? "#FFFFFF",
              borderRadius: 12,
              padding: "1px 9px",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {stage.count}
          </span>
        </div>
        <button
          onClick={onClose}
          aria-label="Close drill-down panel"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#9CA3AF",
            padding: 4,
            lineHeight: 1,
            fontSize: 16,
          }}
        >
          &times;
        </button>
      </div>

      {stage.accounts.length === 0 ? (
        <p style={{ fontSize: 13, color: "#9CA3AF", margin: 0 }}>
          No accounts in this stage for the selected period.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {stage.accounts.map((a) => (
            <li key={a.id} style={{ marginBottom: 6 }}>
              <a
                href={`#/account/${a.id}`}
                style={{
                  fontSize: 13,
                  color: "#0B0B0F",
                  textDecoration: "none",
                  display: "block",
                  padding: "5px 8px",
                  borderRadius: 6,
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "#F0FDF4")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "#F9FAFB")}
              >
                {a.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Period selector ──────────────────────────────────────────────────────────

function PeriodSelector({
  value,
  onChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: "5px 12px",
            borderRadius: 6,
            border: "1px solid",
            borderColor: value === opt.value ? TEAL : "#E5E7EB",
            background: value === opt.value ? TEAL : "#FFFFFF",
            color: value === opt.value ? "#FFFFFF" : "#6B7280",
            fontSize: 12,
            fontWeight: value === opt.value ? 700 : 500,
            cursor: "pointer",
            transition: "all 0.12s",
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function FunnelSkeleton() {
  return (
    <div style={{ display: "flex", gap: 6, width: "100%", height: 160 }}>
      {[100, 88, 76, 64].map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            background: "#F3F4F6",
            borderRadius: 4,
            opacity: 0.7,
            height: h,
            margin: "auto 0",
            animation: "pulse 1.6s ease-in-out infinite",
          }}
        />
      ))}
    </div>
  );
}

// ─── Conversion stats bar ─────────────────────────────────────────────────────

function ConversionBar({ conversions }: { conversions: FunnelConversions }) {
  const items = [
    { label: "MQL → SQL", value: conversions.mql_to_sql },
    { label: "SQL → Opp", value: conversions.sql_to_opp },
    { label: "Opp → Won", value: conversions.opp_to_won },
    { label: "Overall (Won/MQL)", value: conversions.overall },
  ];
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        background: "#F0FDFA",
        border: `1px solid #99F6E4`,
        borderRadius: 8,
        padding: "12px 16px",
      }}
    >
      {items.map((item) => (
        <div key={item.label} style={{ display: "flex", flexDirection: "column", minWidth: 100 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6B7280" }}>
            {item.label}
          </span>
          <span style={{ fontSize: 22, fontWeight: 800, color: TEAL, lineHeight: 1.2 }}>
            {item.value != null ? `${item.value}%` : "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function FunnelPage() {
  const [period, setPeriod] = useState<Period>("current_quarter");
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStageKey, setActiveStageKey] = useState<string | null>(null);

  const fetchData = useCallback(async (p: Period) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<FunnelResponse>(`/api/funnel?period=${p}`);
      setData(result);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load funnel data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
  }, [period, fetchData]);

  const activeStage = data?.stages.find((s) => s.key === activeStageKey) ?? null;

  function handleStageClick(key: string) {
    setActiveStageKey((prev) => (prev === key ? null : key));
  }

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Funnel" }]}
      pageTitle="Funnel"
      pageMeta="MQL to Won conversion funnel — M-PACT method"
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "20px 16px",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {/* Page header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: "#0B0B0F",
                margin: 0,
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              Funnel
            </h1>
            <p style={{ marginTop: 3, fontSize: 13, color: "#6B7280" }}>
              MQL &rarr; SQL &rarr; Opportunity &rarr; Won &middot; Click a stage to drill into accounts.
            </p>
          </div>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Period label */}
        {data && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9CA3AF",
              marginBottom: 16,
            }}
          >
            {data.period.label}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 8,
              padding: "14px 16px",
              color: "#991B1B",
              fontSize: 13,
              marginBottom: 20,
            }}
          >
            {error}
          </div>
        )}

        {/* Main content */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
          {/* Funnel + stats */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* SVG funnel */}
            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 10,
                padding: "20px 16px 16px",
                marginBottom: 16,
              }}
            >
              {loading ? (
                <FunnelSkeleton />
              ) : data && data.stages.length > 0 ? (
                <>
                  <FunnelSVG
                    stages={data.stages}
                    conversions={data.conversions}
                    onStageClick={handleStageClick}
                    activeKey={activeStageKey}
                  />
                  <p
                    style={{
                      margin: "10px 0 0",
                      textAlign: "center",
                      fontSize: 11,
                      color: "#9CA3AF",
                    }}
                  >
                    Click a stage to view account list &middot; Conversion rates shown between stages
                  </p>
                </>
              ) : (
                // W66.X12: Professional empty state — render the full funnel shape
                // with zero counts and dash placeholders. The view should not collapse
                // to a blank box when there is no activity yet.
                <>
                  <FunnelSVG
                    stages={[
                      { key: "MQL",         label: "MQL",         count: 0, accounts: [] },
                      { key: "SQL",         label: "SQL",         count: 0, accounts: [] },
                      { key: "Opportunity", label: "Opportunity", count: 0, accounts: [] },
                      { key: "Won",         label: "Won",         count: 0, accounts: [] },
                    ]}
                    conversions={{
                      mql_to_sql: null as any,
                      sql_to_opp: null as any,
                      opp_to_won: null as any,
                      overall:    null as any,
                    }}
                    onStageClick={() => {}}
                    activeKey={null}
                  />
                  <p
                    style={{
                      margin: "10px 0 0",
                      textAlign: "center",
                      fontSize: 11,
                      color: "#9CA3AF",
                    }}
                  >
                    No qualified activity in this period yet &middot; The funnel will populate as MQLs are accepted and advanced
                  </p>
                </>
              )}
            </div>

            {/* Conversion stats */}
            {data && !loading && (
              <ConversionBar conversions={data.conversions} />
            )}

            {/* Stage count pills */}
            {data && !loading && (
              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                {data.stages.map((stage) => (
                  <button
                    key={stage.key}
                    onClick={() => handleStageClick(stage.key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "6px 14px",
                      borderRadius: 20,
                      border: `2px solid ${activeStageKey === stage.key ? "#0B0B0F" : STAGE_COLORS[stage.key]}`,
                      background: STAGE_COLORS[stage.key],
                      color: STAGE_TEXT[stage.key] ?? "#FFFFFF",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      transition: "all 0.12s",
                      outline: "none",
                    }}
                    aria-pressed={activeStageKey === stage.key}
                  >
                    {stage.label}
                    <span
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {stage.count}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Drill-down panel */}
          {activeStage && (
            <DrillPanel
              stage={activeStage}
              onClose={() => setActiveStageKey(null)}
            />
          )}
        </div>

        {/* Definition footnote */}
        <div
          style={{
            marginTop: 28,
            padding: "12px 14px",
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            fontSize: 11,
            color: "#9CA3AF",
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: "#6B7280" }}>Stage definitions (W66.H):</strong>{" "}
          MQL = accounts in prospect_snapshot with bdr_qualified_at IS NULL (marketing-sourced, not yet BDR-qualified).
          SQL = accounts with bdr_qualified_at IS NOT NULL.
          Opportunity = accounts at status Qualified Opp or Full Opportunity (progression_snapshot).
          Won = accounts at status Won.
          Counts are cumulative for the selected period. Simulation accounts excluded.
        </div>
      </div>
    </OperatorShell>
  );
}
